/**
 * iTrace Serialization Service
 *
 * Handles the 3-step authentication flow for the iTrace Serialization System:
 *   1. POST /api/v1/auth/token/        → access_token
 *   2. POST /serialization/api/plant-batches → Plant Batch (harvest data)
 *   3. POST /api/v1/service-tokens/    → single-use UUID for portal auto-login
 *
 * Portal URL pattern: https://serialisation-dev.itrace.app/?token=<uuid>
 *
 * Reference: Kapitani_Integration-Serialization (1).md
 */

const ITRACE_DJANGO_URL = 'https://django-itrace-1059606892232.asia-southeast1.run.app';
const ITRACE_PORTAL_URL = 'https://serialisation-dev.itrace.app';
const ITRACE_TRACE_URL_BASE = 'https://pwa-qrtrace-dev.web.app/c';  // each QR label encodes this + traceCode

// The iTrace Serialization backend is HTTP-only (port 8080). Mixed-content blocks
// direct calls from an HTTPS page, so in the browser we always go through the
// Vercel serverless proxy at /api/itrace-serialization?path=...
// During local Jest-style tests / non-browser contexts this value is still safe —
// the proxy exists both on localhost (via CRA proxy-dev) and in production.
const ITRACE_SERIALIZATION_DIRECT = 'http://190.92.203.168:8080/serialization/api';
const ITRACE_SERIALIZATION_PROXY = '/api/itrace-serialization?path=';

/**
 * Read a fetch Response as JSON, but guard against the upstream returning an
 * HTML error page (e.g. unauthenticated login redirect, 502 from the proxy,
 * 404 SPA fallback when /api/* isn't actually deployed). Without this, every
 * such failure surfaces as the confusing
 *   "Unexpected token '<', '<!doctype'... is not valid JSON"
 * instead of the actual HTTP status the user needs to debug.
 */
async function safeJson(response, label) {
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`${label} failed (HTTP ${response.status}): ${text.slice(0, 300)}`);
  }
  const ct = (response.headers.get('content-type') || '').toLowerCase();
  if (!ct.includes('application/json')) {
    const text = await response.text();
    const hint = text.trim().startsWith('<') ? ' (got HTML — upstream proxy may be down or returning a login redirect)' : '';
    throw new Error(`${label}: expected JSON, got ${ct || 'no content-type'}${hint}`);
  }
  return response.json();
}

/**
 * Build the URL the browser actually fetches. Uses the Vercel proxy for everything
 * — it works on both localhost (`npm start` runs the /api/ functions via Vercel CLI
 * or falls back to a local fetch) and production.
 */
function serializationUrl(relativePath) {
  // Typical inputs: "plant-batches?siteId=166", "commission/20260424033726/codes"
  if (typeof window === 'undefined') {
    // SSR/test path — use direct URL
    return `${ITRACE_SERIALIZATION_DIRECT}/${relativePath}`;
  }
  return `${ITRACE_SERIALIZATION_PROXY}${encodeURIComponent(relativePath)}`;
}

const ITRACE_CLIENT_ID = 'aC4NUdGzDKNxmvehVdBNT0HxeWc6wESSv6GO0pET';
const ITRACE_SITE_USER = 'sirimSiteAdmin@gmail.com';
const ITRACE_SITE_PASSWORD = 'SirimSitePasswd123';

// Company prefix + Item reference used in Plant Batch EPC codes (observed from existing batches)
const SIRIM_COMPANY_PREFIX = 'C00162';
const SIRIM_ITEM_REFERENCE = 'MD2489';

class ITraceSerializationService {
  constructor() {
    this.djangoUrl = ITRACE_DJANGO_URL;
    this.portalUrl = ITRACE_PORTAL_URL;
    this.debug = process.env.NODE_ENV === 'development';
  }

  // All serialization backend calls go through the Vercel proxy — `relativePath`
  // is the part after `/serialization/api/`, e.g. "plant-batches?siteId=166".
  _serializationUrl(relativePath) {
    return serializationUrl(relativePath);
  }

  log(message, data = null) {
    if (!this.debug) return;
    if (data) console.log(`🔗 [iTrace] ${message}`, data);
    else console.log(`🔗 [iTrace] ${message}`);
  }

  /**
   * Step 1: Get access token using site admin credentials.
   * Token validity: 604,800 seconds (7 days).
   */
  async getAccessToken() {
    this.log('Step 1: Requesting access token');
    const response = await fetch(`${this.djangoUrl}/api/v1/auth/token/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        grant_type: 'password',
        client_id: ITRACE_CLIENT_ID,
        username: ITRACE_SITE_USER,
        username_type: 'email',
        password: ITRACE_SITE_PASSWORD,
      }),
    });

    const data = await safeJson(response, 'iTrace auth');
    this.log('Step 1 ✓ access_token received');
    return data.access_token;
  }

  /**
   * Step 2: Create a Plant Batch that represents this harvest.
   * The Plant Batch is what a Commission will later be linked to in the iTrace portal.
   *
   * When `batch.cycleId` is provided the EPC serial embeds it
   * (`...MD2489.<cycleId>-<timestamp>`). This is what later lets
   * getLabelsForCycle() resolve cycle → commission purely from the
   * commission's batchEpc — the backend removed GET /plant-batches
   * (500 "Request method 'GET' is not supported", observed Jul 2026),
   * so matching by batch description is no longer possible.
   *
   * @param {string} accessToken - Bearer token from Step 1
   * @param {{ batchCode?: string, batchName: string, description?: string, cycleId?: string|number }} batch
   */
  async createPlantBatch(accessToken, batch) {
    this.log('Step 2: Creating plant batch', batch);
    const serial = batch.cycleId != null
      ? `${batch.cycleId}-${Date.now()}`
      : `${Date.now()}`;
    const batchCode =
      batch.batchCode ||
      `urn:epc:id:sgtin:${SIRIM_COMPANY_PREFIX}.${SIRIM_ITEM_REFERENCE}.${serial}`;

    const response = await fetch(this._serializationUrl('plant-batches'), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        batchCode,
        batchName: batch.batchName,
        description: batch.description || '',
      }),
    });

    const result = await safeJson(response, 'Plant Batch creation');
    this.log('Step 2 ✓ plant batch created', result);
    return result?.data || result;
  }

  /**
   * Step 3: Generate a single-use service token (UUID) for portal auto-login.
   */
  async generateServiceToken(accessToken) {
    this.log('Step 3: Generating service token');
    const response = await fetch(`${this.djangoUrl}/api/v1/service-tokens/`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    const data = await safeJson(response, 'Service token generation');
    this.log('Step 3 ✓ service token uuid received');
    return data.uuid;
  }

  /**
   * Build the auto-login portal URL with the service token attached.
   */
  buildPortalUrl(serviceTokenUuid) {
    return `${this.portalUrl}/?token=${encodeURIComponent(serviceTokenUuid)}`;
  }

  /**
   * End-to-end: Send harvest to iTrace and return everything needed to open the portal.
   * This is the method callers will use from the End Cycle flow.
   * Pass `cycleId` so the plant batch EPC embeds it (see createPlantBatch).
   *
   * @param {{ batchName: string, description?: string, batchCode?: string, cycleId?: string|number }} batch
   * @returns {Promise<{ plantBatch: object, serviceTokenUuid: string, portalUrl: string }>}
   */
  async sendHarvestToITrace(batch) {
    const accessToken = await this.getAccessToken();
    const plantBatch = await this.createPlantBatch(accessToken, batch);
    const serviceTokenUuid = await this.generateServiceToken(accessToken);
    return {
      plantBatch,
      serviceTokenUuid,
      portalUrl: this.buildPortalUrl(serviceTokenUuid),
    };
  }

  /**
   * Return the full QR payload URL for a given trace code.
   * This is what each QR image encodes — scanning it sends the consumer to the trace PWA.
   */
  buildTraceUrl(traceCode) {
    return `${ITRACE_TRACE_URL_BASE}/${traceCode}`;
  }

  /**
   * List all plant batches under this site.
   * Jul 2026 backend update: GET /plant-batches was removed; listing moved to
   * GET /batches?type=plant (discovered from the portal's own network calls —
   * it also supports &keyword= search and &excludeAssigned=true).
   */
  async listPlantBatches(accessToken, siteId = 166) {
    const response = await fetch(
      this._serializationUrl(`batches?page=1&pageSize=200&siteId=${siteId}&type=plant`),
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    const result = await safeJson(response, 'List plant batches');
    return result?.data || [];
  }

  /**
   * List all commissions created under this site.
   * Useful to look up the commission linked to a given plant batch EPC.
   * NOTE: the backend defaults to page size 10 and silently truncates —
   * request a large page explicitly so older commissions aren't missed.
   */
  async listCommissions(accessToken, siteId = 166) {
    const response = await fetch(
      this._serializationUrl(`commission?page=1&size=200&sortBy=createdAt&sortDirection=desc&siteId=${siteId}`),
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    const result = await safeJson(response, 'List commissions');
    return result?.data || [];
  }

  /**
   * Fetch the list of label trace codes for a commission.
   * Returns [{ traceCode, status }] — build the full URL with buildTraceUrl(traceCode).
   */
  async getCommissionCodes(accessToken, commissionId) {
    const response = await fetch(
      this._serializationUrl(`commission/${encodeURIComponent(commissionId)}/codes`),
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    const result = await safeJson(response, 'Get commission codes');
    return result?.data || [];
  }

  /**
   * High-level helper: given a Plant Batch EPC (from a SIRIM ended cycle), resolve to the
   * linked commission and its trace codes. Returns `null` if no commission is linked yet.
   */
  async getLabelsForPlantBatch(plantBatchEpc) {
    const accessToken = await this.getAccessToken();
    const commissions = await this.listCommissions(accessToken);
    // Jul 2026 backend update renamed the field plantBatchEpc → batchEpc.
    const commission = commissions.find(c => (c.batchEpc || c.plantBatchEpc) === plantBatchEpc);
    if (!commission) return { commission: null, codes: [] };
    const codes = await this.getCommissionCodes(accessToken, commission.commissionId);
    return {
      commission,
      codes: codes.map(c => ({
        traceCode: c.traceCode,
        status: c.status,
        traceUrl: this.buildTraceUrl(c.traceCode),
      })),
    };
  }

  /**
   * Find the iTrace Plant Batch that corresponds to a SIRIM cycle id, then resolve
   * to its linked Commission (if any) and the full list of QR trace codes.
   *
   * How it matches (a cycle sent to iTrace more than once has several batches):
   *   - batches created since Jul 2026 embed the cycle id in the EPC serial
   *     (…MD2489.<cycleId>-<timestamp>), and every commission carries its batchEpc;
   *   - older batches only carry "Cycle: <id>" in their description, so the batch
   *     list is read too and commissions are matched on the batch code.
   *
   * Returns:
   *   { plantBatch | null, commission | null, codes: [{ traceCode, status, traceUrl }] }
   *
   * Possible states:
   *   - plantBatch=null  → user has not ended this cycle via iTrace yet
   *   - commission=null  → batch exists but no commission linked yet (open portal to create one)
   *   - codes.length > 0 → ready to display QR images (the cycle's newest commission)
   */
  async getLabelsForCycle(cycleId, siteId = 166) {
    const accessToken = await this.getAccessToken();

    // Read-only: commissions and batches are loaded together. If the batch
    // listing is unavailable, degrade to matching on the commission's batchEpc.
    let batchListError = null;
    const [commissions, batches] = await Promise.all([
      this.listCommissions(accessToken, siteId),
      this.listPlantBatches(accessToken, siteId).catch((e) => {
        this.log('batch listing unavailable — commissions-only resolution', e.message);
        batchListError = e;
        return [];
      }),
    ]);

    const serialRe = new RegExp(`\\.${cycleId}-\\d+$`);
    // The "Cycle: <id>" field exactly as the End Cycle flow writes it
    // ("Farm: … | Cycle: <id> | Variant: …"): a whole field, so cycle 345 does not
    // match "Cycle: 3456" and free text in the notes does not match either.
    const markerRe = new RegExp(`(?:^|\\| )Cycle: ${cycleId}(?= \\||$)`);
    const newestFirst = (a, b) => new Date(b.createdAt) - new Date(a.createdAt);

    // Every batch that belongs to this cycle, not just the newest one.
    const cycleBatches = batches
      .filter(b => markerRe.test(b.description || '') || serialRe.test(b.batchCode || ''))
      .sort(newestFirst);
    const cycleBatchCodes = cycleBatches.map(b => b.batchCode).filter(Boolean);

    // Commissions linked to one of those batches (Jul 2026 backend update
    // renamed the field plantBatchEpc → batchEpc); the newest one is shown.
    const matchingComms = commissions
      .filter((c) => {
        const epc = c.batchEpc || c.plantBatchEpc || '';
        return serialRe.test(epc) || cycleBatchCodes.includes(epc);
      })
      .sort(newestFirst);

    if (matchingComms.length > 0) {
      const commission = matchingComms[0];
      const epc = commission.batchEpc || commission.plantBatchEpc;
      const rawCodes = await this.getCommissionCodes(accessToken, commission.commissionId);
      return {
        plantBatch: { batchCode: epc, batchName: commission.commissionName },
        commission,
        codes: rawCodes.map(c => ({
          traceCode: c.traceCode,
          status: c.status,
          traceUrl: this.buildTraceUrl(c.traceCode),
        })),
      };
    }

    // Without the batch list an older cycle cannot be matched at all, so report
    // the real failure rather than "no Plant Batch found".
    if (batchListError) throw batchListError;

    // Batch exists but none of the cycle's batches has a commission yet (or the
    // cycle was never sent to iTrace → plantBatch null).
    return { plantBatch: cycleBatches[0] || null, commission: null, codes: [] };
  }

  /**
   * Open the portal for a user who already has a plant batch and just wants to access iTrace
   * (e.g. to create a new Commission, download existing QR labels).
   */
  async openPortal() {
    const accessToken = await this.getAccessToken();
    const serviceTokenUuid = await this.generateServiceToken(accessToken);
    return {
      serviceTokenUuid,
      portalUrl: this.buildPortalUrl(serviceTokenUuid),
    };
  }

  /**
   * Open the portal in a new tab without tripping pop-up blockers.
   * Call this directly from a click handler: the empty tab is opened right away
   * (before the first await, so still inside the click) and is pointed at the
   * portal once the one-time token arrives. Throws when the browser refuses the
   * tab or when iTrace cannot issue the token (the empty tab is closed first).
   */
  async openPortalInNewTab() {
    const tab = window.open('', '_blank');
    if (!tab) {
      throw new Error('Your browser blocked the new tab. Allow pop-ups for this site, then click the button again.');
    }
    try {
      const { portalUrl } = await this.openPortal();
      if (tab.closed) return; // user closed the empty tab while waiting
      tab.opener = null; // same isolation the 'noopener' flag gave
      tab.location.replace(portalUrl);
    } catch (e) {
      tab.close();
      throw e;
    }
  }
}

const itraceSerializationService = new ITraceSerializationService();
export default itraceSerializationService;
export { ITraceSerializationService };
